import Observer from '~/utils/lib/core/observer'

/**
* @typedef {object} AccountSessionData
* @property {string} accessToken
* @property {string} refreshToken
* @property {number} clientVersion
* @property {string} updatedAt stringified milliseconds UNIX timestamps
* @property {string} expiredAt idem - Client-side expiration
*/

/**
 * @typedef {object} RequestConstructorParams
 * @property {string} options.method
 * @property {string} options.route
 * @property {object} [options.data]
 * @property {object} [options.params] will hydrate URL params
 */

/**
 * @typedef {object} RequestResponse
 * @property {*} data
 * @property {number} status
 * @property {string} statusText
 * @property {object} headers
 * @property {RequestConfig} config
 * @property {XMLHttpRequest} request
 * @link https://axios-http.com/docs/res_schema
 */

/**
 * @typedef {Error & {
 *    config: RequestConfig
 *    isAxiosError: boolean
 *    request?: XMLHttpRequest
 *    response?: RequestResponse
 * }} RequestError
 * @desc extending `Error` with instance extra properties
 * @link https://axios-http.com/docs/handling_errors
 */

/**
 * @typedef {import('axios').AxiosRequestConfig & {
 *  withAuthorization: boolean;
 *  execCount: number;
 * }} RequestConfig
 * @desc extending Axios config with extra config properties
 * @link https://axios-http.com/docs/req_config
 */

/**
 * (?) Cache storage client version
 *
 *     * bump it when a new cache data need to be reset (eg. following
 *       changes that evolve cache storage format)
 *     * see "core" plugins for more details on cache reset following
 *       client version management)
 *
 * @constant {number}
 * @default
 */
export const CLIENT_VERSION = 10

/**
 * (?) When creating a persistent cache data, this constant will be added
 *     to set the expiration delay
 *
 * @constant {number}
 * @default 1814400000 // 3 weeks (in milliseconds)
 */
export const CACHE_MAX_PERIOD = 1814400000

/**
 * @constant {string}
 * @default
 */
export const STORE_PREFIX = 'account'

/** @constant {string} */
export const baseURL = import.meta.env.VITE_ACCOUNT_API_URL

/** @constant {object} */
export const EVENTS = {
  ON_SIGNIN: 'account/onSignin',
  ON_SIGNOUT: 'account/onSignout',
  ON_RESTORATION: 'account/onRestoration',
  ON_STORE_SESSION_DESTROY: 'account/onStoreSessionDestroy',
  ON_REFRESH_POLICY_INFRINGEMENT: 'account/onRefreshPolicyInfringement',
  ON_DISPATCH_SIGNIN: 'account/onDispatchSignin',
  ON_SUPER_ADMIN_SIGNIN: 'account/onSuperAdminSignin',
}

/** @constant {object} */
export const ERRORS = {
  CACHE_EXPIRATION_REACHED: 'stored authentication has reached expiration',
  CACHE_INTEGRITY_COMPROMISED: 'issue in stored cache data integrity',
  CACHE_MAJOR_VERSION_MISMATCHED: 'stored cache authentication version mismatched',
  NO_CACHE_RETRIEVED: 'no authentication data has been retrieved in cache',
  NO_MFA_TOKEN: 'no mfa token has been found',
  REQUEST_RESPONSE_UNKNOWN_SCHEMA: '<RequestError> with unknown schema',
  REQUEST_RESPONSE_UNKNOWN_DATA: '<RequestError.data> with unknown schema',
  REQUEST_RESPONSE_UNKNOWN_MESSAGE: '<RequestError.data> with unknown "message"',
  REQUEST_RESPONSE_UNKNOWN_CAUSE: '<RequestError.data> with unknown "cause"',
}

/**
 * @constant {object}
 * @readonly
 */
export const MICRO_SERVICE_ERRORS = /** @type {const} */ ({
  MISC: 'miscelleanous micro service error returned',
  // @XXX account backend micro-service: best to obfuscate the login error
  LOGIN_ACCOUNT_NOT_FOUND: 'CompteNotFound',
  LOGIN_INCORRECT_PASSWORD: 'MdpIncorrect',
})

/** @type {Observer} */
export const observer = new Observer()
